import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IFormValues,
  IUcApiRequest,
  IUcApiResponse,
} from "../../shared/interfaces";
import { useApi } from "../../hooks/useApi";
import { CheckboxChangeEvent } from "primereact/checkbox";
import { TOAST_TIME_TIME } from "../../shared/constants";
import { ClientTypeEnum } from "../../shared/enums";

export function useConfirmationScreen() {
  const { call } = useApi();
  const navigate = useNavigate();
  const {
    state: { request, response },
  } = useLocation() as {
    state: { request: IUcApiRequest; response: IUcApiResponse };
  };

  const [firstMotherName, setFirstMotherName] = useState<string>("");
  const [buildingNumber, setBuildingNumber] = useState<string>("");
  const [isPredialNumberEmpty, setIsPredialNumberEmpty] =
    useState<boolean>(false);

  const unitNumber = useMemo(
    () =>
      String(
        request?.consultaUcRequest?.ucAntiga ||
          request?.consultaUcRequest?.ucANEEL ||
          ""
      ),
    [request?.consultaUcRequest]
  );
  const validateBuildingNumber = (): boolean => {
    const buildingNumberResponse = response?.consultaUcResponse?.numeroPredial
      ?.toString()
      .trim()
      .toLowerCase();

    if (isPredialNumberEmpty) {
      return (
        !buildingNumberResponse && buildingNumber.trim().toLowerCase() === ""
      );
    }

    return buildingNumberResponse === buildingNumber.trim().toLowerCase();
  };

  const validateFirstMotherName = (): boolean => {
    const isIndividualPerson =
      response.consultaUcResponse.tipoPessoa === ClientTypeEnum.INDIVIDUAL;

    if (!isIndividualPerson) {
      return true;
    }

    const motherNameResponse = response?.consultaUcResponse?.primeiroNomeDaMae
      ?.toString()
      .trim()
      .toLowerCase();

    return motherNameResponse === firstMotherName.trim().toLowerCase();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    call({
      loading: true,
      try: async (toast) => {
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);

        const formValues: IFormValues = {};
        formData.forEach((value, key) => {
          formValues[key] = value.toString();
        });

        const doesBuildingNumberMatch = validateBuildingNumber();

        const doesMotherNameMatch = validateFirstMotherName();

        if (doesMotherNameMatch && doesBuildingNumberMatch) {
          navigate("/result", { state: { response } });
        } else {
          toast({
            severity: "error",
            summary: "Erro na confirmação",
            detail:
              "O primeiro nome da mãe ou o número predial não conferem com os nossos registros.",
            life: TOAST_TIME_TIME,
          });
        }
      },
    });
  };

  const onBackToUcSearch = () => {
    navigate("/");
  };

  const handleFirstMotherNameChange = (e: {
    target: {
      value: string;
      name?: string;
    };
  }) => {
    setFirstMotherName(e.target.value.toUpperCase());
  };

  const handlePredialNumberEmptyChange = (e: CheckboxChangeEvent) => {
    const isChecked = e.checked ?? false;
    setIsPredialNumberEmpty(isChecked);
    if (isChecked) {
      setBuildingNumber("");
    }
  };

  return {
    response,
    firstMotherName,
    setFirstMotherName,
    handleSubmit,
    handleFirstMotherNameChange,
    buildingNumber,
    setBuildingNumber,
    unitNumber,
    onBackToUcSearch,
    isPredialNumberEmpty,
    setIsPredialNumberEmpty,
    handlePredialNumberEmptyChange,
  };
}
